/**
 * Production Console Override
 * 
 * When PRODUCTION_MODE is enabled, this module disables all console output
 * by replacing console methods with no-ops.
 * 
 * This handles the existing 2300+ direct console.* calls throughout the codebase
 * without requiring individual changes to each file.
 * 
 * IMPORTANT: Import this module as early as possible in your app entry point (App.tsx)
 * 
 * Usage:
 *   // In App.tsx (at the very top)
 *   import './utils/productionConsole';
 * 
 * Configuration:
 *   Set EXPO_PUBLIC_PRODUCTION_MODE=true in your .env file
 */

import { APP_CONFIG } from '../config/appConfig';

// Store original console methods for potential restoration
const originalConsole = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  debug: console.debug,
  info: console.info,
  trace: console.trace,
  dir: console.dir,
  dirxml: console.dirxml,
  table: console.table,
  group: console.group,
  groupCollapsed: console.groupCollapsed,
  groupEnd: console.groupEnd,
  time: console.time,
  timeEnd: console.timeEnd,
  timeLog: console.timeLog,
  assert: console.assert,
  count: console.count,
  countReset: console.countReset,
  clear: console.clear,
};

// No-op function
const noop = () => {};

/**
 * Disable all console output
 * Called automatically when PRODUCTION_MODE is true
 */
const disableConsole = (): void => {
  console.log = noop;
  console.warn = noop;
  console.error = noop;
  console.debug = noop;
  console.info = noop;
  console.trace = noop;
  console.dir = noop;
  console.dirxml = noop;
  console.table = noop;
  console.group = noop;
  console.groupCollapsed = noop;
  console.groupEnd = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.timeLog = noop;
  console.assert = noop;
  console.count = noop;
  console.countReset = noop;
  // Keep console.clear functional even in production
  // console.clear = noop;
};

/**
 * Restore original console methods
 * Use this for debugging production issues if needed
 */
export const restoreConsole = (): void => {
  console.log = originalConsole.log;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.debug = originalConsole.debug;
  console.info = originalConsole.info;
  console.trace = originalConsole.trace;
  console.dir = originalConsole.dir;
  console.dirxml = originalConsole.dirxml;
  console.table = originalConsole.table;
  console.group = originalConsole.group;
  console.groupCollapsed = originalConsole.groupCollapsed;
  console.groupEnd = originalConsole.groupEnd;
  console.time = originalConsole.time;
  console.timeEnd = originalConsole.timeEnd;
  console.timeLog = originalConsole.timeLog;
  console.assert = originalConsole.assert;
  console.count = originalConsole.count;
  console.countReset = originalConsole.countReset;
};

/**
 * Get original console for special cases
 * Use this when you absolutely need to log something even in production
 * (e.g., critical security alerts to external logging services)
 */
export const getOriginalConsole = () => originalConsole;

/**
 * Check if console is currently disabled
 */
export const isConsoleDisabled = (): boolean => APP_CONFIG.PRODUCTION_MODE;

// Auto-initialize based on production mode
if (APP_CONFIG.PRODUCTION_MODE) {
  // Log one final message before disabling (useful for debugging)
  originalConsole.log(
    '🔇 [ProductionConsole] Production mode enabled - all console output disabled'
  );
  disableConsole();
} else {
  // In development, log that console is active
  if (__DEV__) {
    console.log('📝 [ProductionConsole] Development mode - console logging active');
  }
}

export default {
  disable: disableConsole,
  restore: restoreConsole,
  getOriginal: getOriginalConsole,
  isDisabled: isConsoleDisabled,
};

